<script setup lang="ts">
import {consumerCartApi} from "@/container";
import {onMounted, ref, watch} from "vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import DrugSelectionCartCardRow
    from "@/drug-interaction/components/drug-selection/prescription/DrugSelectionCartCardRow.vue";
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {ConsumerCart, ConsumerCartRequest} from "@/core/http/resources/ConsumerCartResource";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {SourceType} from "@/consumer/interfaces";

const props = defineProps<{
    consumerKey: string
    selectedProducts: ProductSummary[]
    prescriptors?: number[];
    initialCarts?: ConsumerCart[] | null;
    initialCartsLoading?: boolean;
    initialCartAfterKey?: string | null;
}>();

const localCarts = ref<ConsumerCart[] | null>(props.initialCarts ?? null);
const loading = ref(props.initialCartsLoading ?? false);
const loadingMore = ref(false);
const meta = ref<{ after_key?: string } | null>(props.initialCartAfterKey ? {after_key: props.initialCartAfterKey} : null);

const abort = ref<AbortController | undefined>(undefined);

watch(() => props.initialCarts, (value) => {
    if (value !== undefined) {
        localCarts.value = value;
    }
});

watch(() => props.initialCartsLoading, (value) => {
    if (value !== undefined) {
        loading.value = value;
    }
});

onMounted(() => {
    if (!localCarts.value && !loading.value) {
        loading.value = true;
        loadCarts(null, props.prescriptors?.length ? {doctors: props.prescriptors} : {})
            .finally(() => {
                loading.value = false;
            });
    }
});

/**
 * Load consumer carts.
 */
const loadCarts = async (afterKey: null | string = null, query: ConsumerCartRequest = {}) => {
    if (abort.value) {
        abort.value.abort();
        abort.value = undefined;
    }

    abort.value = new AbortController();

    return consumerCartApi()
        .index(props.consumerKey, afterKey, query, abort.value)
        .then((response) => {
            meta.value = response.meta;
            localCarts.value = [...(localCarts.value ?? []), ...response.data];
        });
};

/**
 * Load more carts.
 */
const loadMore = () => {
    if (meta.value && meta.value.after_key) {
        loadingMore.value = true;
        loadCarts(meta.value.after_key, props.prescriptors?.length ? {doctors: props.prescriptors} : {})
            .finally(() => {
                loadingMore.value = false;
            });
    }
};

watch(() => props.prescriptors, () => {
    localCarts.value = null;
    meta.value = null;
    loading.value = true;
    loadCarts(null, props.prescriptors?.length ? {doctors: props.prescriptors} : {})
        .finally(() => {
            loading.value = false;
        });
});


defineEmits<{
    (e: 'select', product: ProductSummary, source: SourceType): void;
}>();
</script>

<template>
  <div>
    <div v-if="(loading) || !localCarts">
      <BaseFlexSpinner />
    </div>
    <div v-else-if="localCarts">
      <div v-if="!localCarts.length" class="text-center">
        <p>Aucune produit trouvé.</p>
      </div>
      <div v-else class="flex flex-col gap-6">
        <div v-for="cart in localCarts" :key="cart.key">
          <DrugSelectionCartCardRow
            :cart="cart"
            :selected-products="selectedProducts"
            @select="(product) => $emit(
              'select',
              product,
              cart.has_prescription ? SourceType.PRESCRIPTION : SourceType.SELF_MEDICATION
            )"
          />
        </div>
        <BaseButton v-if="meta?.after_key" primary class="self-center" :loading="loadingMore" @click="loadMore">
          Voir plus
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
